import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from 'flowbite-react';
import classNames from 'classnames';
import { UserAvailabilityInput } from 'types';
import { allWeekDays } from 'shared-utils';
import dayjs, { Dayjs } from 'dayjs';

type SlotOption = {
	label: string;
	startTime: Dayjs;
	endTime: Dayjs;
};
type Props = {
	duration: number;
	date: Date;
	availabilities: UserAvailabilityInput[];
	availabilitiesByDate: UserAvailabilityInput[];
	setAvailabilities: (availabilities: UserAvailabilityInput[]) => void;
};

const totalMins = 24 * 60;
const noonMinutes = 12 * 60;

const DateSlotsSelections = ({
	duration,
	date,
	availabilities = [],
	availabilitiesByDate = [],
	setAvailabilities,
}: Props) => {
	const weekday = allWeekDays[date.getDay()];
	const slotDate = dayjs(date).startOf('day');
	const [loadingSlot, setLoadingSlot] = useState<SlotOption | null>();
	const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);

	const addSlot = async (slotStartDateTime: Dayjs, slotEndDateTime: Dayjs) => {
		try {
			setLoadingSlot({ label: '', startTime: slotStartDateTime, endTime: slotEndDateTime });
			// Simulate an asynchronous operation (you can replace this with your actual logic)
			await new Promise(resolve => setTimeout(resolve, 1000));
			setAvailabilities([
				...availabilities,
				{
					day: weekday,
					duration,
					startTime: slotStartDateTime.toDate(),
					endTime: slotEndDateTime.toDate(),
					isBooked: false,
				},
			]);
		} finally {
			setLoadingSlot(null);
		}
	};

	const removeSlot = useCallback(
		async (slotStartDateTime: Dayjs, slotEndDateTime: Dayjs) => {
			try {
				setLoadingSlot({ label: '', startTime: slotStartDateTime, endTime: slotEndDateTime });
				// Simulate an asynchronous operation (you can replace this with your actual logic)
				await new Promise(resolve => setTimeout(resolve, 1000));

				let otherSlots = availabilities.filter(a => {
					return !(slotStartDateTime.isSame(a.startTime) && slotEndDateTime.isSame(a.endTime));
				});
				setAvailabilities(otherSlots);
			} finally {
				setLoadingSlot(null);
			}
		},
		[availabilities, setAvailabilities],
	);

	useEffect(() => {
		const totalOptions = Math.floor(totalMins / duration);
		let newSlots: SlotOption[] = Array(totalOptions)
			.fill(0)
			.map((_, index) => {
				const currMinutes: number = index * duration;
				let slotTime;
				if (currMinutes < noonMinutes) {
					const hrs = Math.floor(currMinutes / 60);
					const remainingMinutes = currMinutes - hrs * 60;
					slotTime = `${hrs || '12'}:${remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes}am`;
				} else {
					const minutes = currMinutes - noonMinutes;
					const hrs = Math.floor(minutes / 60);
					const remainingMinutes = minutes - hrs * 60;
					slotTime = `${hrs || '12'}:${remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes}pm`;
				}
				return {
					startTime: dayjs(slotDate).add(currMinutes, 'minutes'),
					endTime: dayjs(slotDate).add(currMinutes + duration, 'minutes'),
					label: slotTime,
				};
			});
		setSlotOptions(newSlots);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [duration]);

	return (
		<div className="flex flex-wrap gap-2">
			{slotOptions.map((slotOption, index) => {
				const slotStartDate = slotOption.startTime.toDate();
				const slotEndDate = slotOption.endTime.toDate();
				const start = dayjs(slotStartDate);
				const end = dayjs(slotEndDate);
				const isPastSlot = start.isBefore(dayjs());
				const isOverlappingBookedSlot = availabilities.find(a => {
					if (!a.isBooked) return false;
					const aStart = dayjs(a.startTime);
					const aEnd = dayjs(a.endTime);
					// const aStart = moment(a.startTime);
					// const aEnd = moment(a.endTime);
					// return false;
					return (
						(aStart.isBefore(slotStartDate) && aEnd.isAfter(slotEndDate)) || (aStart.isBefore(end) && aEnd.isAfter(end))
					);
				});
				let selectedSlot = availabilitiesByDate.find(a => {
					return dayjs(a.startTime).isSame(start) && dayjs(a.endTime).isSame(end);
				});
				// const isPastSlot = false;
				// const isOverlappingBookedSlot = false;
				// const selectedSlot = {
				// 	isBooked: Math.random()*10<2,
				// }
				return (
					<Badge
						key={index}
						className={classNames('w-24 cursor-pointer justify-center', {
							'hover:cursor-not-allowed':
								isPastSlot || isOverlappingBookedSlot || (selectedSlot && selectedSlot.isBooked),
						})}
						color={
							isPastSlot
								? 'dark'
								: isOverlappingBookedSlot
								  ? 'failure'
								  : selectedSlot
								    ? selectedSlot.isBooked
											? 'failure'
											: 'success'
								    : 'warning'
						}
						onClick={async () => {
							if (isPastSlot || isOverlappingBookedSlot || (selectedSlot && selectedSlot.isBooked)) return;
							if (selectedSlot && !selectedSlot.isBooked) {
								await removeSlot(slotOption.startTime, slotOption.endTime);
							} else {
								await addSlot(slotOption.startTime, slotOption.endTime);
							}
						}}
					>
						{loadingSlot && loadingSlot.startTime === slotOption.startTime && loadingSlot.endTime === slotOption.endTime
							? 'Loading...'
							: slotOption.startTime.format('hh:mm a')}
					</Badge>
				);
			})}
		</div>
	);
};

export default DateSlotsSelections;
